import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PrivacySection = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const privacyTopics = [
    {
      title: 'Data Collection & Usage',
      icon: 'Database',
      content: 'We collect only essential information required to provide our services: employee profile data, usage analytics, and communication records. All data is encrypted at rest and in transit using AES-256 encryption.'
    },
    {
      title: 'GDPR Compliance',
      icon: 'Shield',
      content: 'Full compliance with EU General Data Protection Regulation. Users have the right to access, rectify, delete, and port their personal data. Data processing agreements available for all enterprise customers.'
    },
    {
      title: 'Cookie Policy',
      icon: 'Cookie',
      content: 'We use essential cookies for authentication and analytics cookies (with consent) to improve user experience. No third-party advertising cookies. Users can manage preferences via cookie consent manager.'
    },
    {
      title: 'Data Retention',
      icon: 'Clock',
      content: 'Active account data retained for service provision duration. Deleted accounts undergo 30-day soft delete period, then permanent deletion. Backup data retained for 90 days for disaster recovery.'
    },
    {
      title: 'Third-Party Sharing',
      icon: 'Share2',
      content: 'Data shared only with essential service providers (cloud hosting, analytics) under strict DPAs. No data sold to third parties. All sub-processors listed and regularly audited.'
    },
    {
      title: 'User Rights & Controls',
      icon: 'Settings',
      content: 'Dashboard access to view, export, and delete personal data. Granular privacy controls for profile visibility. Dedicated privacy inbox: privacy@firstondate.com'
    },
  ];

  return (
    <section id="privacy" className="scroll-mt-20">
      <div className="bg-card/30 backdrop-blur-sm border border-border rounded-2xl p-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center">
            <Icon name="Shield" size={32} className="text-success" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-foreground">Privacy Policy</h2>
            <p className="text-muted-foreground mt-1">Last updated: January 15, 2025</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none mb-8">
          <p className="text-lg text-muted-foreground leading-relaxed">
            At First On Date, we take your privacy seriously. This policy outlines how we collect, use, protect, and respect your personal data in compliance with GDPR, CCPA, and other international privacy regulations.
          </p>
        </div>

        <div className="space-y-4">
          {privacyTopics?.map((topic, index) => (
            <div 
              key={index}
              className="bg-background/50 border border-border rounded-lg overflow-hidden hover:border-success/50 transition-colors"
            >
              <button
                onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-card/30 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name={topic?.icon} size={20} className="text-success" />
                  </div>
                  <span className="font-semibold text-foreground">{topic?.title}</span>
                </div>
                <Icon 
                  name={expandedSection === index ? 'ChevronUp' : 'ChevronDown'} 
                  size={20} 
                  className="text-muted-foreground"
                />
              </button>
              
              {expandedSection === index && (
                <div className="px-6 pb-6">
                  <p className="text-muted-foreground leading-relaxed pl-14">
                    {topic?.content}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 p-6 bg-success/5 border border-success/20 rounded-lg">
          <div className="flex items-start gap-4">
            <Icon name="CheckCircle" size={24} className="text-success flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-foreground mb-2">Privacy Commitment</h3>
              <p className="text-sm text-muted-foreground">
                We conduct annual third-party privacy audits and maintain ISO 27701 Privacy Information Management certification. Questions? Contact our Data Protection Officer at dpo@firstondate.com
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-4">
          <Button 
            variant="default"
            iconName="Download"
            iconPosition="left"
            onClick={() => window.open('/privacy-policy.pdf', '_blank')}
          >
            Download Full Policy (PDF)
          </Button>
          <Button 
            variant="outline"
            iconName="Mail"
            iconPosition="left"
            onClick={() => window.open('mailto:privacy@firstondate.com', '_blank')}
          >
            Contact Privacy Team
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PrivacySection;